import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';
import {
  IQuotation,
  IQuotationDetail,
  OrderStatus,
  OrderStatusStyle,
  SendOrderPayload,
} from '../models/quotes.models';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { OrderFilterRequest } from 'src/app/shared/helper';
import { PageParams } from 'src/app/shared/models/request';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private notificationService: NotificationService
  ) {}

  public getOrders(
    page?: PageParams,
    filter?: OrderFilterRequest | undefined,
    key?: string | undefined
  ): Observable<APIResponse<IQuotation>> {
    let params = new HttpParams();
    if (page?.number !== undefined) params = params.set('page', page.number);
    if (page?.size !== undefined) params = params.set('limit', page.size);
    if (filter !== undefined) params = params.set('filters', JSON.stringify(filter));
    if (key !== undefined) params = params.set('key', key);
    return this.http.get<APIResponse<IQuotation>>(
      environment.BASE_URL_API + 'retailers/quotations',
      { params }
    );
  }

  public getOrderById(id: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.get<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + `retailers/quotations/${id}`
    );
  }

  public sendOrderOrRfq(
    data: SendOrderPayload,
  ): Observable<APIResponse<IQuotation>> {
    return this.http.post<APIResponse<IQuotation>>(
      environment.BASE_URL_API + `retailers/quotations/${data.cartId}/send-rfq-or-order`, data
    );
  }

  public updateOrder(orderId: number, updates: Partial<IQuotationDetail>): Observable<any> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      `${environment.BASE_URL_API}retailers/quotations/${orderId}/edit-and-ask`,
      updates
    );
  }

  public getOrderDocument(id: number): Observable<Blob> {
    return this.http.get<Blob>(environment.BASE_URL_API + `retailers/quotations/${id}/pdf`, {
      responseType: 'blob' as 'json',
    });
  }

  public getOrdersExport(ordersIds: number[]): Observable<Blob> {
    return this.http.post<Blob>(
      environment.BASE_URL_API + 'retailers/quotations/export',
      { orders_ids: ordersIds },
      { responseType: 'blob' as 'json' }
    );
  }

  public downloadOrderDocument(order: IQuotation): Observable<void> {
    return new Observable((observer) => {
      this.getOrderDocument(order.id).subscribe({
        next: (res) => {
          const blob = new Blob([res], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          // Add current date in YYYYMMDD format to the filename
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const dateStr = `${year}${month}${day}`;

          // Add current time in HHMMSS format to the filename
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');
          const timeStr = `${hours}${minutes}${seconds}`;

          link.download = `order-${order.name}-${dateStr}-${timeStr}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.notificationService.show('Order document downloaded successfully', 'success');
          observer.next();
          observer.complete();
        },
        error: (error) => {
          console.error('Error downloading order document:', error);
          this.notificationService.show('Error downloading file', 'error');
          observer.error(error);
        },
      });
    });
  }

  public exportOrders(ordersIds: number[]): Observable<void> {
    return new Observable((observer) => {
      this.getOrdersExport(ordersIds).subscribe({
        next: (res) => {
          const blob = new Blob([res], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          // Add current date in YYYYMMDD format to the filename
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const dateStr = `${year}${month}${day}`;

          // Add current time in HHMMSS format to the filename
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');
          const timeStr = `${hours}${minutes}${seconds}`;

          link.download = `orders-${dateStr}-${timeStr}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.notificationService.show('Order document downloaded successfully', 'success');
          observer.next();
          observer.complete();
        },
        error: (error) => {
          console.error('Error downloading order document:', error);
          this.notificationService.show('Error downloading file', 'error');
          observer.error(error);
        },
      });
    });
  }

  reorder(orderId: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      `${environment.BASE_URL_API}retailers/quotations/${orderId}/reorder`,
      {}
    );
  }

  getStatusTranslation(status: string): string {
    return this.translateService.instant(`quotes_and_order.QuotationStatus.${status}`) || status;
  }

  getStatusStyle(status: OrderStatus): OrderStatusStyle {
    switch (status) {
      case 'draft':
        return 'secondary';
      case 'requested':
      case 'sent':
      case 'to_be_validated':
        return 'info';
      case 'pending_inventory':
        return 'warning';
      case 'declined':
      case 'cancel':
      case 'deleted':
        return 'danger';
      case 'create_accepted_order':
        return 'success';
      case 'sale':
      case 'done':
        return 'success';
      default:
        return 'secondary';
    }
  }
}
